import React, { useState, useMemo } from 'react';
import { Archive, RotateCcw, X, AlertTriangle } from 'lucide-react';

const PolicyActionsModal = ({ customer, onAction, onClose }) => {
    // State for modal tabs and form inputs
    const [activeTab, setActiveTab] = useState('Renew');
    const [terminationDate, setTerminationDate] = useState(new Date().toISOString().split('T')[0]);
    const [newExpirationDate, setNewExpirationDate] = useState('');
    const [newPolicyNumber, setNewPolicyNumber] = useState(customer.policy || '');
    // Convert premium string (e.g., "620 250 FCFA") to a number for the input field
    const [newPremium, setNewPremium] = useState(customer.premium.replace(/[^0-9]/g, '') || ''); 
    
    // Calculate 1 year from the current expiration date for the renewal suggestion
    const nextYearExpiration = useMemo(() => {
        const currentExpiryString = customer.expiry; 
        if (!currentExpiryString || currentExpiryString === 'N/A') return new Date().toISOString().split('T')[0];
        
        // Convert DD/MM/YYYY to YYYY-MM-DD for correct Date object creation
        const parts = currentExpiryString.split('/');
        const isoDateString = `${parts[2]}-${parts[1]}-${parts[0]}`; 

        const date = new Date(isoDateString);
        // Add one year to the current expiration date
        date.setFullYear(date.getFullYear() + 1);
        
        // Format back to YYYY-MM-DD for the HTML input[type="date"]
        return date.toISOString().split('T')[0];
    }, [customer]);

    // Set the suggested date when the modal opens
    useState(() => {
        setNewExpirationDate(nextYearExpiration);
    }, [nextYearExpiration]);

    const handleRenew = (e) => {
        e.preventDefault();
        
        // Format premium back to standard display format
        const formattedPremium = `${Number(newPremium).toLocaleString()} FCFA`;
        
        // Get date in DD/MM/YYYY format to match existing data
        const [year, month, day] = newExpirationDate.split('-');
        const formattedExpiry = `${day}/${month}/${year}`;
        
        const renewalData = {
            "Action": "Renew",
            "policy": newPolicyNumber,
            "premium": formattedPremium,
            "expiry": formattedExpiry,
            "status": "Active",
            "expired": false, // Renewal makes it active and not expired
        };
        onAction(customer.id, renewalData, "Successfully renewed contract.");
    };

    const handleTerminate = (e) => {
        e.preventDefault();
        
        // Get date in DD/MM/YYYY format for logging termination
        const [year, month, day] = terminationDate.split('-');
        const formattedTerminationDate = `${day}/${month}/${year}`;
        
        const terminationData = {
            "Action": "Terminate",
            "status": "Inactive",
            "terminationDate": formattedTerminationDate, // Log the termination date
            "expired": true, // A terminated contract is considered inactive/expired
        };
        onAction(customer.id, terminationData, "Successfully terminated contract and marked as inactive.");
    };

    const handleArchive = () => {
        if (!window.confirm(`Are you sure you want to archive the policy for ${customer.name}? This will permanently remove it from the active list.`)) {
            return;
        }
        
        // Archiving requires the policy to be non-Active
        if (customer.status === 'Active') {
            alert("A contract must be terminated or expired before it can be archived.");
            return;
        }
        
        // Action is a DELETE from the active list in the parent component
        onAction(customer.id, { "Action": "Archive" }, "Contract successfully archived.");
    };

    // Determine if the current policy is eligible for archiving
    const canArchive = customer.status !== 'Active';

    const renderRenewTab = () => (
        <form onSubmit={handleRenew} className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-800">Policy Renewal</h3>
            <p className="text-sm text-gray-600">This action will create a new contract period based on the updated details below.</p>
            
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Policy Number (New)</label>
                    <input 
                        type="text" 
                        value={newPolicyNumber} 
                        onChange={(e) => setNewPolicyNumber(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2" 
                        required 
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">New Expiration Date</label>
                    <input 
                        type="date" 
                        value={newExpirationDate} 
                        onChange={(e) => setNewExpirationDate(e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md p-2" 
                        required 
                    />
                </div>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">Net Premium (XAF) (New)</label>
                <input 
                    type="number" 
                    value={newPremium} 
                    onChange={(e) => setNewPremium(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2" 
                    required 
                />
            </div>
            
            <button
                type="submit"
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 transition"
            >
                <RotateCcw className="w-5 h-5 mr-2" /> Confirm Renewal
            </button>
        </form>
    );

    const renderTerminateTab = () => (
        <form onSubmit={handleTerminate} className="space-y-4">
            <h3 className="text-xl font-semibold  text-red-600">Contract Termination</h3>
            <p className="text-sm text-gray-600 border-l-4 border-red-400 pl-3 py-1 bg-red-50">
                <AlertTriangle className="w-4 h-4 inline mr-1 text-red-500"/> This action will mark the contract as **Inactive/Terminated**.
            </p>
            
            <div>
                <label className="block text-sm font-medium text-gray-700">Date of Termination</label>
                <input 
                    type="date" 
                    value={terminationDate} 
                    onChange={(e) => setTerminationDate(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md p-2" 
                    required 
                />
            </div>

            <button
                type="submit"
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition"
            >
                <X className="w-5 h-5 mr-2" /> Terminate Contract
            </button>
        </form>
    );

    const renderArchiveTab = () => (
        <div className="space-y-4">
            <h3 className="text-xl font-semibold text-gray-800">Archive Policy Record</h3>
            <p className="text-sm text-gray-600 border-l-4 border-blue-400 pl-3 py-1 bg-blue-50">
                Archiving moves the full customer record out of the active list. It must be **Inactive** or **Expired**.
            </p>
            
            <div className="bg-gray-100 p-4 rounded-lg">
                <p className="font-medium">Current Status: 
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded ${canArchive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {customer.status}
                    </span>
                </p>
                {!canArchive && (
                    <p className="text-sm text-red-600 mt-2">Policy must be terminated or expired before archiving.</p>
                )}
            </div>

            <button
                onClick={handleArchive}
                disabled={!canArchive}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 transition disabled:opacity-50"
            >
                <Archive className="w-5 h-5 mr-2" /> Archive Record
            </button>
        </div>
    );

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center border-b pb-3 mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Manage Policy: {customer.name}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                
                {/* Tabs Navigation */}
                <div className="flex border-b mb-6">
                    {['Renew', 'Terminate', 'Archive'].map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            className={`px-4 py-2 -mb-px text-sm font-medium border-b-2 transition duration-150 ${
                                activeTab === tab 
                                    ? "border-gray-900 text-gray-900 font-semibold" 
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                            }`}
                            onClick={() => setActiveTab(tab)}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'Renew' && renderRenewTab()}
                {activeTab === 'Terminate' && renderTerminateTab()}
                {activeTab === 'Archive' && renderArchiveTab()}
            </div>
        </div>
    );
};

export default PolicyActionsModal;